import {PageStateChecker} from "../classes/misc/page-state-checker";

/**
 Poll the server for changes to this page and reload it automatically.
 @param initialStateValue (optional) the initial ajax state value to compare with the server.
 @param altPageStateChecker (optional) a PageStateChecker object to use instead of the default.
 */
export function setupAjax(initialStateValue, altPageStateChecker) {
    // since we use document.location.reload() we cannot support AJAX refresh on a page that
    // was loaded as a result of a form POST.  (by convention, it is not safe to resubmit a form POST)
    if (!isPOSTed()) {

        if (!altPageStateChecker) altPageStateChecker = new PageStateChecker();

        if (typeof initialStateValue == "number")
            altPageStateChecker.initialSystemState = initialStateValue;

        // Start the polling in a few milliseconds
        //adamb: previously we hard-coded the first check to 500ms. However, customers with busy
        // communicators pages wish to have a much slower refresh by setting ajax.seconds in
        // config.properties. Our default for ajax.seconds is 5 seconds.
        let firstCheckMillis = 500;
        if (altPageStateChecker.pollRateSeconds > 0 ) {
            firstCheckMillis = altPageStateChecker.pollRateSeconds * 1000;
        }

        GlobalEnt.ajax.pollTimer = window.setTimeout(
            altPageStateChecker.startNextCheck.bind(altPageStateChecker),
            firstCheckMillis,
        );

        // Avoid ajax refresh if the mouse has recently moved.
        let events = ["mousedown", "mousemove", "keypress", "scroll"];
        events.forEach(function (name) {
            document.addEventListener(name, ajaxOnActivity, true);
        });
    }
}

/**
 Make an Ajax request to a URL only after the delay period has elapsed.  This allows for more efficient use of
 Ajax because it allows batching of requests.

 Constructor Parameters
 postUrl: the URL to POST the request to
 postBodyBuilder: an object that has a buildPOSTBody() method which returns the data to be posted
 delayMs: the number of milliseconds that must elapse before the request is actually made (after the last call to dataReady()).
 ajaxManager: an ajaxManager

 [methods]
 dataReady(): call this when there is data ready to be posted.  After the delay period has elapsed the buildPOSTBody() method
 of postBodyBuilder will be called and the request will be submitted.
 */
export function DelayedAjaxPost(postUrl, postBodyBuilder, delayMs, ajaxManager, unused)
{
    this.delayMs = delayMs;
    this.postUrl = postUrl;
    this.postBodyBuilder = postBodyBuilder;
    this.ajaxManager = ajaxManager;
    this.curReq = null;
    this.timeout = null;

    this.dataReady = function() {
        if (this.timeout != null) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }

        let self = this;  //closure letiable
        this.timeout = setTimeout(function(){self._onTimeout()}, this.delayMs);
    }

    this._onTimeout = function() {
        //not currently sending a request?
        if (this.curReq == null)
            this._sendRequest();
        else {
            //try again later
            this.dataReady();
        }
    }

    this._sendRequest = function() {
        let postData = this.postBodyBuilder.buildPOSTBody();

        this.curReq = ajaxManager.createRequest();
        if (this.curReq != null) {
            let self = this;  //closure letiable
            postAjaxRequest(this.postUrl, postData, function() {self.curReq = null;}, false, this.curReq);
        }
    }
}

/**
 Like makeAjaxRequest except it uses the POST method rather than GET and
 it allows very large amounts of text data to be uploaded.

 @param nameValuePairs an array where every two elements is a name and value string.
 These will be accessible on the server side via request.getParameter("some_name")
 */
export function postAjaxRequest(url, nameValuePairs, callback_function, return_xml, anXMLHTTPRequest) {
    let http_request;
    if (anXMLHTTPRequest)
        http_request = anXMLHTTPRequest;
    else
        http_request = createAjaxRequest(false);

    if (!http_request)
        return false;

    http_request.onreadystatechange = function() {
        if (http_request.readyState == 4) {
            if (http_request.status == 200) {
                if (callback_function) {
                    if (return_xml) {
                        callback_function(http_request.responseXML);
                    }
                    else {
                        callback_function(http_request.responseText);
                    }
                }
            }
        }
    }

    //bugfix: trim off hash from URL to prevent server from getting '#...' concatenated to last parameter value.
    //The issue only seems to effect IE which implies to me that XMLHttpRequest.open() is not
    //sanitizing the URL like in other browsers.
    url = new String(url);  //in case Location object was given
    let hashPos = url.lastIndexOf('#');
    if (hashPos > 0)
        url = url.substring(0, hashPos);

    http_request.open('POST', url, true);

    http_request.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

    //build the body of the request
    let postData = '';
    if (typeof(nameValuePairs) == 'string')
        postData = nameValuePairs;
    else {
        let i;
        for (i = 0; i < nameValuePairs.length; i += 2) {
            if (i > 0)
                postData += '&';
            postData += nameValuePairs[i];
            postData += '=';
            postData += encodeURIComponent(nameValuePairs[i + 1]);
        }
    }

    // safari caching workaround -
    // explicitly set to discard cached files newer than this...
    http_request.setRequestHeader('If-Modified-Since', 'Wed, 15 Nov 1995 00:00:00 GMT');

    http_request.send(postData);
    return true;
}

/**
 Construct an AJAX XMLHttpRequest object in a browser portable way.
 */
export function createAjaxRequest(overrideMimeType) {
    let http_request = false;

    // Mozilla / Safari
    if (window.XMLHttpRequest) {
        http_request = new XMLHttpRequest();

        if (overrideMimeType && http_request.overrideMimeType) {
            http_request.overrideMimeType('text/xml');
        }
    }
    // IE
    else if (window.ActiveXObject) {
        try {
            http_request = new ActiveXObject("Msxml2.XMLHTTP");
        }
        catch (e) {
            alert(e);
            try {
                http_request = new ActiveXObject("Microsoft.XMLHTTP");
            }
            catch (e) {
                // no luck
                alert(e);
            }
        }
    }

    return http_request;
}


/**
 Manages XMLHttpRequest objects.  Due to a shortcoming if FireFox XMLHttpRequest
 objects are created ahead of time	so that an instance is created when this object
 is instantiated.  The bug is that if an XMLHttpRequest object is created as a page
 is unloading an exception will be thrown if you attempt to use it.  By creating it
 ahead of time a valid XMLHttpRequest is available when the page unloads.

 [public methods]
 createRequest(): get a new instance of XMLHttpRequest or null if it is not supported.
 isSupported(): determine if XMLHttpRequest is supported
 */
export function AjaxManager() {
    this.createRequest = function() {
        let result = this.req;
        this.req = createAjaxRequest();
        this.ableToCreateReq = this.req != null;
        return result;
    }

    this.isSupported = function() {
        return this.ableToCreateReq;
    }

    this.createRequest();
    this.ableToCreateReq = this.req != null;
}

/** Return true if the current document was loaded as a result of a form POST.  It is not
 "safe" to reload at POSTed document (in fact the browser will prompt the user) */
function isPOSTed() {
    const smartRedirectSupport = document.getElementById('smartRedirectSupport');
    if (smartRedirectSupport) {
        const dataset = smartRedirectSupport.dataset;

        // smartRedirectSupport.tag sets the following variable to true if the page being displayed is a result of a POST.
        // If this variable is undefined we will assume that it's not a post request so that listing pages and such can auto-refresh
        return dataset.requestWasPosted === "true";
    }

    return false;

    // The old logic iterated over all forms in this page to see if any of them had method="POST"
    // with an action attribute of the current document location.  Look in version control to get it back (Jan 09)
}


/**
 Avoid ajax refresh if activity is registered
 */
export function ajaxIsBusy() {
    // True if the activity was recorded within the last 3 seconds
    var now = new Date().getTime();
    return now - GlobalEnt.ajax.lastUserActivityMs < 3000;
}

function ajaxOnActivity(e) {
    GlobalEnt.ajax.lastUserActivityMs = new Date().getTime();
}

/**Request a URL repeatedly with AJAX.  The specified callback_func can return false to stop polling.*/
export function AjaxPoller(pollURL, pollMs, callback_func) {
    this.pollMs = pollMs;
    this.pollURL = pollURL;
    this.callback_func = callback_func;
    this.ajaxManager = new AjaxManager();
    this.curReq = null;
    this.timer = null;

    this.start = function()
    {
        this.stop();  //just in case
        var self = this;  //closure variable
        this.timer = window.setInterval(function() {self._onTimer()}, this.pollMs);
    }

    this.stop = function()
    {
        if (this.timer != null)
        {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    this._onTimer = function()
    {
        //not currently sending a request?
        if (this.curReq == null)
            this._sendRequest();
        //else it will try again later
    }

    this._sendRequest = function()
    {
        this.curReq = this.ajaxManager.createRequest();
        if (this.curReq != null)
        {
            var self = this;  //closure variable

            this.curReq.onreadystatechange = function()
            {
                if (this.readyState == 4)
                {
                    if (this.status == 200)
                    {
                        if (!self.callback_func(this))
                            self.stop();
                    }
                    else
                        self.stop();

                    //let _onTimer back in
                    self.curReq = null;
                }
            };

            this.curReq.open('GET', this.pollURL);
            this.curReq.send(null);
        }
    }
}

export function downloadCSVFileFromServer(url, fileName, closeWindowAfterDownload = true) {
    downloadFileFromServer(url, fileName, 'text/csv;charset=utf-8;', closeWindowAfterDownload);
}

export function downloadFileFromServer(url, fileName, contentType, closeWindowAfterDownload) {
    const { csrfToken } = GlobalEnt.pageData;
    if (!csrfToken) {
        console.error('Failed to retrieve csrfToken for downloading file from server...');
        return;
    }

    const req = createAjaxRequest();
    req['onload'] = () => {
        downloadBlob(req.response, fileName, contentType);
        if (closeWindowAfterDownload) {
            document.addEventListener('focusin', () => {
                window.close();
            });
        }
    }
    req.open('GET', url);
    req.setRequestHeader('_CSRFToken', csrfToken);
    req.send(null);
}

function downloadBlob(content, fileName, contentType) {
    const blob = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(blob);

    const dynamicLink = document.createElement('a');
    dynamicLink.href = url;
    dynamicLink.setAttribute('download', fileName);
    dynamicLink.target = "_blank";
    document.body.appendChild(dynamicLink);
    dynamicLink.click();
    document.body.removeChild(dynamicLink);
    window.URL.revokeObjectURL(url);
}